import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError } from '../api/client.js';
import PageHeader from '../components/PageHeader.jsx';
import LoadingState from '../components/LoadingState.jsx';
import EmptyState from '../components/EmptyState.jsx';
import StatusBadge from '../components/StatusBadge.jsx';
import SettingsTab from '../components/publisher/SettingsTab.jsx';
import DemandPartnersTab from '../components/publisher/DemandPartnersTab.jsx';
import PlacementsTab from '../components/publisher/PlacementsTab.jsx';
import BiddersTab from '../components/publisher/BiddersTab.jsx';
import AbTestsTab from '../components/publisher/AbTestsTab.jsx';
import ConfigVersionsTab from '../components/publisher/ConfigVersionsTab.jsx';

const TABS = [
  { key: 'settings', label: 'Settings' },
  { key: 'demand-partners', label: 'Demand Partners' },
  { key: 'placements', label: 'Placements & Floors' },
  { key: 'bidders', label: 'Bidders' },
  { key: 'ab-tests', label: 'A/B Tests' },
  { key: 'config-versions', label: 'Config Versions' },
];

export default function PublisherDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [publisher, setPublisher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('settings');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const pub = await api.getPublisher(id);
        if (!cancelled) setPublisher(pub);
      } catch (err) {
        if (!cancelled) setError(err instanceof ApiError ? err.message : 'Could not load publisher.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) return <div className="page"><LoadingState label="Loading publisher" /></div>;
  if (error || !publisher) {
    return (
      <div className="page">
        <EmptyState title="Publisher not found" message={error || 'This publisher may have been deleted.'} />
      </div>
    );
  }

  return (
    <div className="page">
      <PageHeader
        title={
          <span className="page-header__title-with-badge">
            {publisher.name}
            <StatusBadge status={publisher.status} />
          </span>
        }
        subtitle={publisher.domain}
        actions={
          <button className="btn btn--ghost" onClick={() => navigate('/publishers')}>
            &larr; All publishers
          </button>
        }
      />

      <div className="tabs">
        {TABS.map((t) => (
          <button key={t.key} className={`tabs__tab${tab === t.key ? ' tabs__tab--active' : ''}`} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="tab-panel">
        {tab === 'settings' && <SettingsTab publisher={publisher} onPublisherChange={setPublisher} />}
        {tab === 'demand-partners' && <DemandPartnersTab publisherId={publisher.id} />}
        {tab === 'placements' && <PlacementsTab publisherId={publisher.id} />}
        {tab === 'bidders' && <BiddersTab publisherId={publisher.id} />}
        {tab === 'ab-tests' && <AbTestsTab publisherId={publisher.id} />}
        {tab === 'config-versions' && <ConfigVersionsTab publisherId={publisher.id} />}
      </div>
    </div>
  );
}
